import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  FaPlus, FaTrash, FaEye, FaSave, FaFont, FaImage, 
  FaFilm, FaRegSquare, FaMinus, FaShareAlt, FaVideo, 
  FaGripVertical, FaCode
} from 'react-icons/fa';
import { MdSpaceBar } from 'react-icons/md';
import { AiOutlineColumnWidth } from 'react-icons/ai';
import { useAuth } from '../../services/check_login';
import './EmailBuilder.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const EmailBuilder = ({ initialTemplate = null, onSave }) => {
  const { user } = useAuth();
  const location = useLocation();
  const templateFromState = location.state?.template;
  
  const [activeTab, setActiveTab] = useState('structure');
  const [previewMode, setPreviewMode] = useState(false);
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [draggedBlockType, setDraggedBlockType] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [templateId, setTemplateId] = useState(null);

  useEffect(() => {
    if (templateFromState) {
      setTemplateName(templateFromState.name || '');
      setRows(templateFromState.structure || []);
      setTemplateId(templateFromState._id || null);
    } else if (initialTemplate) {
      setTemplateName(initialTemplate.name || '');
      setRows(initialTemplate.structure || []);
      setTemplateId(initialTemplate._id || null);
    }
  }, [templateFromState, initialTemplate]);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const basicBlocks = [
    { type: 'text', label: 'Text', icon: FaFont },
    { type: 'image', label: 'Image', icon: FaImage },
    { type: 'gif', label: 'GIF', icon: FaFilm },
    { type: 'button', label: 'Button', icon: FaRegSquare },
    { type: 'divider', label: 'Divider', icon: FaMinus },
    { type: 'spacer', label: 'Spacer', icon: MdSpaceBar },
    { type: 'social', label: 'Social', icon: FaShareAlt },
    { type: 'video', label: 'Video', icon: FaVideo },
    { type: 'html', label: 'HTML', icon: FaCode }
  ];

  const premadeBlocks = [
    { type: 'email-header', label: 'Email Header (Logo + Button)' },
    { type: 'hero', label: 'Hero Section' },
    { type: 'image-text', label: 'Image + Text' },
    { type: 'image-text-button', label: 'Image + Text + Button' },
    { type: 'cta', label: 'Call to Action' }
  ];

  const availablePlaceholders = [
    { value: '{{headline}}', label: 'Headline' },
    { value: '{{body}}', label: 'Body Text' },
    { value: '{{additionalText}}', label: 'Additional Text' },
    { value: '{{additionalTextUnderBTN}}', label: 'Text Under Button' },
    { value: '{{bodyBtnName}}', label: 'Button Name' },
    { value: '{{bodyBtnLink}}', label: 'Button Link' },
    { value: '{{bodyImage}}', label: 'Body Image URL' },
    { value: '{{bodyImageLink}}', label: 'Body Image Link' },
    { value: '{{brand.logo}}', label: 'Brand Logo URL' },
    { value: '{{brand.logoLink}}', label: 'Brand Logo Link' },
    { value: '{{brand.buttonColor}}', label: 'Brand Button Color' },
    { value: '{{brand.buttonTextColor}}', label: 'Brand Button Text Color' },
    { value: '{{snippets.footer}}', label: 'Footer (Small Disclaimer)' },
    { value: '{{snippets.disclaimer}}', label: 'Marketing Disclaimer' }
  ];

  const getDefaultElementContent = (type) => {
    const defaults = {
      text: { text: 'Enter your text here...', align: 'left', fontSize: '16px', color: '#333333' },
      image: { src: '', alt: '', align: 'center', link: '' },
      gif: { src: '', alt: '', align: 'center', link: '' },
      button: { text: 'Click Here', link: '#', align: 'center', bgColor: '{{brand.buttonColor}}', textColor: '{{brand.buttonTextColor}}' },
      divider: { color: '#e0e0e0', height: '1px' },
      spacer: { height: '20px' },
      social: { align: 'center' },
      video: { videoUrl: '', thumbnail: '', alt: '' },
      html: { code: '<p>Custom HTML</p>' },
      hero: { headline: 'Welcome!', subheadline: 'This is a hero section', buttonText: 'Get Started', buttonLink: '#', image: '' },
      'image-text': { heading: 'Feature Title', text: 'Feature description goes here...', image: '' },
      'image-text-button': { heading: 'Feature Title', text: 'Feature description goes here...', buttonText: 'Learn More', buttonLink: '#', image: '' },
      cta: { headline: 'Ready to start?', text: 'Join us today!', buttonText: 'Sign Up', buttonLink: '#', bgColor: '#ffffff' }
    };
    return defaults[type] || {};
  };

  const toggleDropdown = (id, e) => {
    if (e) {
      e.stopPropagation();
    }
    setShowDropdown(showDropdown === id ? null : id);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (showDropdown) {
        setShowDropdown(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  const addRow = (columnCount) => {
    const newRow = {
      id: Date.now().toString(),
      type: columnCount === 1 ? '1-column' : columnCount === 2 ? '2-columns' : '3-columns',
      columns: Array.from({ length: columnCount }, (_, i) => ({
        id: `${Date.now()}-${i}`,
        width: columnCount === 1 ? '100%' : columnCount === 2 ? '50%' : '33.33%',
        elements: []
      }))
    };
    setRows([...rows, newRow]);
  };

  const splitRowIntoColumns = (rowIndex, newColumnCount) => {
    const newRows = [...rows];
    const currentRow = newRows[rowIndex];
    
    const allElements = currentRow.columns.flatMap(col => col.elements);
    
    const newColumns = Array.from({ length: newColumnCount }, (_, i) => ({
      id: `${Date.now()}-${i}`,
      width: newColumnCount === 1 ? '100%' : newColumnCount === 2 ? '50%' : '33.33%',
      elements: i === 0 ? allElements : []
    }));
    
    newRows[rowIndex] = {
      ...currentRow,
      type: newColumnCount === 1 ? '1-column' : newColumnCount === 2 ? '2-columns' : '3-columns',
      columns: newColumns
    };
    
    setRows(newRows);
  };

  const addElementToColumn = (rowIndex, columnIndex, elementType) => {
    // Check if this is a premade block that should create a row structure
    if (elementType === 'email-header') {
      addPremadeHeaderRow();
      return;
    }
    
    const newRows = [...rows];
    const newElement = {
      id: Date.now().toString(),
      type: elementType,
      content: getDefaultElementContent(elementType)
    };
    newRows[rowIndex].columns[columnIndex].elements.push(newElement);
    setRows(newRows);
    setShowDropdown(null);
  };
  
  const addPremadeHeaderRow = () => {
    const headerRow = {
      id: Date.now().toString(),
      type: '2-columns',
      columns: [
        {
          id: `${Date.now()}-0`,
          width: '50%',
          elements: [{
            id: `${Date.now()}-logo`,
            type: 'image',
            content: {
              src: '{{brand.logo}}',
              alt: 'Logo',
              align: 'left',
              link: '{{brand.logoLink}}'
            }
          }]
        },
        {
          id: `${Date.now()}-1`,
          width: '50%',
          elements: [{
            id: `${Date.now()}-btn`,
            type: 'button',
            content: {
              text: 'View in Browser',
              link: '#',
              align: 'right',
              bgColor: '{{brand.buttonColor}}',
              textColor: '{{brand.buttonTextColor}}'
            }
          }]
        }
      ]
    };
    setRows([headerRow, ...rows]);
  };

  const removeRow = (rowIndex) => {
    const newRows = rows.filter((_, index) => index !== rowIndex);
    setRows(newRows);
  };

  const removeElement = (rowIndex, columnIndex, elementIndex) => {
    const newRows = [...rows];
    newRows[rowIndex].columns[columnIndex].elements = 
      newRows[rowIndex].columns[columnIndex].elements.filter((_, index) => index !== elementIndex);
    setRows(newRows);
    setSelectedBlock(null);
  };

  const updateElementContent = (rowIndex, columnIndex, elementIndex, field, value) => {
    const newRows = [...rows];
    newRows[rowIndex].columns[columnIndex].elements[elementIndex].content[field] = value;
    setRows(newRows);
  };

  const handleBlockDragStart = (e, blockType) => {
    e.stopPropagation();
    setDraggedBlockType(blockType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragStart = (e, rowIndex, columnIndex = null, elementIndex = null) => {
    e.stopPropagation();
    setDraggedBlock({ rowIndex, columnIndex, elementIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = draggedBlockType ? 'copy' : 'move';
  };

  const handleColumnDrop = (e, targetRowIndex, targetColumnIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If dragging a new block type from sidebar
    if (draggedBlockType) {
      addElementToColumn(targetRowIndex, targetColumnIndex, draggedBlockType);
      setDraggedBlockType(null);
      return;
    }
    
    // If dragging existing element
    if (draggedBlock && draggedBlock.elementIndex !== null && draggedBlock.columnIndex !== null) {
      const newRows = [...rows];
      const [movedElement] = newRows[draggedBlock.rowIndex].columns[draggedBlock.columnIndex].elements.splice(draggedBlock.elementIndex, 1);
      newRows[targetRowIndex].columns[targetColumnIndex].elements.push(movedElement);
      setRows(newRows);
      setDraggedBlock(null);
    }
  };

  const handleDrop = (e, targetRowIndex, targetColumnIndex = null, targetElementIndex = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedBlock) return;

    const newRows = [...rows];
    
    if (draggedBlock.elementIndex !== null && targetElementIndex !== null && 
        draggedBlock.columnIndex !== null && targetColumnIndex !== null) {
      const [movedElement] = newRows[draggedBlock.rowIndex].columns[draggedBlock.columnIndex].elements.splice(draggedBlock.elementIndex, 1);
      newRows[targetRowIndex].columns[targetColumnIndex].elements.splice(targetElementIndex, 0, movedElement);
    } else if (draggedBlock.columnIndex === null && targetColumnIndex === null && 
               draggedBlock.elementIndex === null && targetElementIndex === null) {
      const [movedRow] = newRows.splice(draggedBlock.rowIndex, 1);
      newRows.splice(targetRowIndex, 0, movedRow);
    }
    
    setRows(newRows);
    setDraggedBlock(null);
  };

  const generatePreviewHTML = () => {
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body { 
            margin: 0; 
            padding: 20px; 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background-color: #ffffff;
          }
          .email-container {
            max-width: 600px;
            margin: 0 auto;
            background: white;
            padding: 20px;
          }
        </style>
      </head>
      <body>
        <div class="email-container">
    `;

    rows.forEach(row => {
      if (row.columns.length > 1) {
        html += `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;"><tr>`;
        row.columns.forEach(column => {
          const columnWidth = parseInt(column.width);
          html += `<td width="${columnWidth}%" style="vertical-align:top;padding:0 10px;">`;
          column.elements.forEach(element => {
            html += generateElementPreviewHTML(element);
          });
          html += `</td>`;
        });
        html += `</tr></table>`;
      } else {
        row.columns.forEach(column => {
          column.elements.forEach(element => {
            html += generateElementPreviewHTML(element);
          });
        });
      }
    });

    html += `
        </div>
      </body>
      </html>
    `;

    return html;
  };

  const generateElementPreviewHTML = (element) => {
    const content = element.content;
    
    switch (element.type) {
      case 'text':
        return `<div style="margin-bottom:15px;"><p style="text-align:${content.align};font-size:${content.fontSize};color:${content.color};margin:0;line-height:1.6;">${content.text}</p></div>`;
      
      case 'image':
      case 'gif':
        if (!content.src) return '';
        return `<div style="text-align:${content.align};margin-bottom:15px;">${content.link ? `<a href="${content.link}">` : ''}<img src="${content.src}" alt="${content.alt}" style="max-width:100%;height:auto;" />${content.link ? '</a>' : ''}</div>`;
      
      case 'button':
        // Replace brand placeholders with default colors for preview
        let btnBg = content.bgColor || '#3cbec6';
        let btnColor = content.textColor || '#ffffff';
        if (btnBg.includes('{{brand.buttonColor}}') || btnBg.includes('{brand.buttonColor}')) {
          btnBg = '#3cbec6';
        }
        if (btnColor.includes('{{brand.buttonTextColor}}') || btnColor.includes('{brand.buttonTextColor}')) {
          btnColor = '#ffffff';
        }
        return `<div style="text-align:${content.align};margin:20px 0;"><a href="${content.link}" style="display:inline-block;padding:12px 30px;background-color:${btnBg};color:${btnColor};text-decoration:none;border-radius:4px;font-weight:600;">${content.text}</a></div>`;
      
      case 'divider':
        return `<div style="margin:20px 0;"><hr style="border:0;border-top:${content.height} solid ${content.color};" /></div>`;
      
      case 'spacer':
        return `<div style="height:${content.height};"></div>`;
      
      case 'hero':
        return `<div style="text-align:center;padding:40px 20px;margin-bottom:20px;">${content.image ? `<img src="${content.image}" alt="Hero" style="max-width:100%;height:auto;margin-bottom:25px;" />` : ''}<h1 style="margin:0 0 15px 0;font-size:36px;color:#333;font-weight:700;">${content.headline}</h1><p style="font-size:18px;color:#666;margin:0 0 25px 0;">${content.subheadline}</p>${content.buttonText ? `<a href="${content.buttonLink}" style="display:inline-block;padding:14px 32px;background-color:#3cbec6;color:#ffffff;text-decoration:none;border-radius:4px;font-weight:600;">${content.buttonText}</a>` : ''}</div>`;
      
      case 'image-text':
        return `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;"><tr><td width="45%" style="vertical-align:top;padding-right:20px;">${content.image ? `<img src="${content.image}" alt="${content.heading}" style="max-width:100%;height:auto;" />` : ''}</td><td width="55%" style="vertical-align:middle;"><h3 style="margin:0 0 12px 0;font-size:24px;color:#333;">${content.heading}</h3><p style="margin:0;color:#666;line-height:1.6;">${content.text}</p></td></tr></table>`;
      
      case 'image-text-button':
        return `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;"><tr><td width="45%" style="vertical-align:top;padding-right:20px;">${content.image ? `<img src="${content.image}" alt="${content.heading}" style="max-width:100%;height:auto;" />` : ''}</td><td width="55%" style="vertical-align:middle;"><h3 style="margin:0 0 12px 0;font-size:24px;color:#333;">${content.heading}</h3><p style="margin:0 0 18px 0;color:#666;line-height:1.6;">${content.text}</p>${content.buttonText ? `<a href="${content.buttonLink}" style="display:inline-block;padding:12px 28px;background-color:#3cbec6;color:#ffffff;text-decoration:none;border-radius:4px;font-weight:600;">${content.buttonText}</a>` : ''}</td></tr></table>`;
      
      case 'cta':
        return `<div style="text-align:center;padding:35px 25px;background-color:${content.bgColor};border-radius:8px;margin-bottom:20px;"><h2 style="margin:0 0 12px 0;color:#333;font-size:28px;">${content.headline}</h2><p style="margin:0 0 22px 0;color:#666;font-size:16px;">${content.text}</p>${content.buttonText ? `<a href="${content.buttonLink}" style="display:inline-block;padding:14px 32px;background-color:#3cbec6;color:#ffffff;text-decoration:none;border-radius:4px;font-weight:600;">${content.buttonText}</a>` : ''}</div>`;
      
      case 'social':
        return `<div style="text-align:${content.align};margin:20px 0;"><p style="color:#999;font-size:14px;">Social Media Icons</p></div>`;
      
      case 'video':
        return `<div style="text-align:center;margin-bottom:20px;">${content.thumbnail ? `<a href="${content.videoUrl}"><img src="${content.thumbnail}" alt="${content.alt}" style="max-width:100%;height:auto;" /></a>` : `<p style="color:#999;">Video: ${content.videoUrl}</p>`}</div>`;
      
      case 'html':
        return content.code;
      
      default:
        return '';
    }
  };

  const renderElementEditor = (element, rowIndex, columnIndex, elementIndex) => {
    const content = element.content;
    const updateContent = (field, value) => updateElementContent(rowIndex, columnIndex, elementIndex, field, value);

    switch (element.type) {
      case 'text':
        return (
          <div className="element-editor">
            <label>
              Text
              <div style={{ marginBottom: '8px' }}>
                <select 
                  onChange={(e) => {
                    if (e.target.value) {
                      updateContent('text', (content.text || '') + e.target.value);
                      e.target.value = '';
                    }
                  }}
                  style={{ 
                    padding: '6px 10px', 
                    fontSize: '12px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd',
                    width: '100%',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">+ Insert Placeholder</option>
                  {availablePlaceholders.map(ph => (
                    <option key={ph.value} value={ph.value}>{ph.label} ({ph.value})</option>
                  ))}
                </select>
              </div>
            </label>
            <textarea
              value={content.text}
              onChange={(e) => updateContent('text', e.target.value)}
              rows="3"
              placeholder="Enter text..."
            />
            <div className="editor-row">
              <label>
                Align
                <select value={content.align} onChange={(e) => updateContent('align', e.target.value)}>
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </label>
              <label>
                Size
                <input type="text" value={content.fontSize} onChange={(e) => updateContent('fontSize', e.target.value)} />
              </label>
              <label>
                Color
                <input type="color" value={content.color} onChange={(e) => updateContent('color', e.target.value)} />
              </label>
            </div>
          </div>
        );

      case 'image':
      case 'gif':
        return (
          <div className="element-editor">
            <label>
              Image URL
              <div style={{ marginBottom: '8px' }}>
                <select 
                  onChange={(e) => {
                    if (e.target.value) {
                      updateContent('src', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  style={{ 
                    padding: '6px 10px', 
                    fontSize: '12px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd',
                    width: '100%',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">+ Insert Placeholder</option>
                  <option value="{{bodyImage}}">Body Image</option>
                </select>
              </div>
              <input type="text" value={content.src} onChange={(e) => updateContent('src', e.target.value)} placeholder="https://..." />
            </label>
            <label>
              Alt Text
              <input type="text" value={content.alt} onChange={(e) => updateContent('alt', e.target.value)} />
            </label>
            <label>
              Link (optional)
              <div style={{ marginBottom: '8px' }}>
                <select 
                  onChange={(e) => {
                    if (e.target.value) {
                      updateContent('link', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  style={{ 
                    padding: '6px 10px', 
                    fontSize: '12px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd',
                    width: '100%',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">+ Insert Placeholder</option>
                  <option value="{{bodyImageLink}}">Body Image Link</option>
                </select>
              </div>
              <input type="text" value={content.link} onChange={(e) => updateContent('link', e.target.value)} placeholder="https://..." />
            </label>
            <label>
              Align
              <select value={content.align} onChange={(e) => updateContent('align', e.target.value)}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </label>
          </div>
        );

      case 'button':
        return (
          <div className="element-editor">
            <label>
              Button Text
              <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                <select 
                  onChange={(e) => {
                    if (e.target.value) {
                      updateContent('text', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">+ Insert Placeholder</option>
                  <option value="{{bodyBtnName}}">Button Name</option>
                </select>
              </div>
              <input type="text" value={content.text} onChange={(e) => updateContent('text', e.target.value)} />
            </label>
            <label>
              Link
              <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                <select 
                  onChange={(e) => {
                    if (e.target.value) {
                      updateContent('link', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">+ Insert Placeholder</option>
                  <option value="{{bodyBtnLink}}">Button Link</option>
                </select>
              </div>
              <input type="text" value={content.link} onChange={(e) => updateContent('link', e.target.value)} placeholder="https://..." />
            </label>
            <div className="editor-row">
              <label>
                Align
                <select value={content.align} onChange={(e) => updateContent('align', e.target.value)}>
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </label>
              <label>
                BG Color
                <input type="color" value={content.bgColor} onChange={(e) => updateContent('bgColor', e.target.value)} />
              </label>
              <label>
                Text Color
                <input type="color" value={content.textColor} onChange={(e) => updateContent('textColor', e.target.value)} />
              </label>
            </div>
          </div>
        );

      case 'divider':
        return (
          <div className="element-editor">
            <div className="editor-row">
              <label>
                Color
                <input type="color" value={content.color} onChange={(e) => updateContent('color', e.target.value)} />
              </label>
              <label>
                Height
                <input type="text" value={content.height} onChange={(e) => updateContent('height', e.target.value)} />
              </label>
            </div>
          </div>
        );

      case 'spacer':
        return (
          <div className="element-editor">
            <label>
              Height
              <input type="text" value={content.height} onChange={(e) => updateContent('height', e.target.value)} placeholder="20px" />
            </label>
          </div>
        );

      case 'hero':
        return (
          <div className="element-editor">
            <label>
              Image URL (optional)
              <div style={{ marginBottom: '8px' }}>
                <select onChange={(e) => { if (e.target.value) { updateContent('image', e.target.value); e.target.value = ''; }}} style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', cursor: 'pointer' }}>
                  <option value="">+ Insert Placeholder</option>
                  <option value="{{bodyImage}}">Body Image</option>
                </select>
              </div>
              <input type="text" value={content.image} onChange={(e) => updateContent('image', e.target.value)} placeholder="https://..." />
            </label>
            <label>
              Headline
              <div style={{ marginBottom: '8px' }}>
                <select onChange={(e) => { if (e.target.value) { updateContent('headline', e.target.value); e.target.value = ''; }}} style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', cursor: 'pointer' }}>
                  <option value="">+ Insert Placeholder</option>
                  <option value="{{headline}}">Headline</option>
                </select>
              </div>
              <input type="text" value={content.headline} onChange={(e) => updateContent('headline', e.target.value)} />
            </label>
            <label>
              Subheadline
              <div style={{ marginBottom: '8px' }}>
                <select onChange={(e) => { if (e.target.value) { updateContent('subheadline', e.target.value); e.target.value = ''; }}} style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', cursor: 'pointer' }}>
                  <option value="">+ Insert Placeholder</option>
                  <option value="{{body}}">Body Text</option>
                  <option value="{{additionalText}}">Additional Text</option>
                </select>
              </div>
              <textarea value={content.subheadline} onChange={(e) => updateContent('subheadline', e.target.value)} rows="2" />
            </label>
            <label>
              Button Text
              <div style={{ marginBottom: '8px' }}>
                <select onChange={(e) => { if (e.target.value) { updateContent('buttonText', e.target.value); e.target.value = ''; }}} style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', cursor: 'pointer' }}>
                  <option value="">+ Insert Placeholder</option>
                  <option value="{{bodyBtnName}}">Button Name</option>
                </select>
              </div>
              <input type="text" value={content.buttonText} onChange={(e) => updateContent('buttonText', e.target.value)} />
            </label>
            <label>
              Button Link
              <div style={{ marginBottom: '8px' }}>
                <select onChange={(e) => { if (e.target.value) { updateContent('buttonLink', e.target.value); e.target.value = ''; }}} style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', cursor: 'pointer' }}>
                  <option value="">+ Insert Placeholder</option>
                  <option value="{{bodyBtnLink}}">Button Link</option>
                </select>
              </div>
              <input type="text" value={content.buttonLink} onChange={(e) => updateContent('buttonLink', e.target.value)} placeholder="https://..." />
            </label>
          </div>
        );

      case 'image-text':
        return (
          <div className="element-editor">
            <label>
              Image URL
              <input type="text" value={content.image} onChange={(e) => updateContent('image', e.target.value)} placeholder="https://..." />
            </label>
            <label>
              Heading
              <input type="text" value={content.heading} onChange={(e) => updateContent('heading', e.target.value)} />
            </label>
            <label>
              Text
              <textarea value={content.text} onChange={(e) => updateContent('text', e.target.value)} rows="3" />
            </label>
          </div>
        );

      case 'image-text-button':
        return (
          <div className="element-editor">
            <label>
              Image URL
              <input type="text" value={content.image} onChange={(e) => updateContent('image', e.target.value)} placeholder="https://..." />
            </label>
            <label>
              Heading
              <input type="text" value={content.heading} onChange={(e) => updateContent('heading', e.target.value)} />
            </label>
            <label>
              Text
              <textarea value={content.text} onChange={(e) => updateContent('text', e.target.value)} rows="3" />
            </label>
            <label>
              Button Text
              <input type="text" value={content.buttonText} onChange={(e) => updateContent('buttonText', e.target.value)} />
            </label>
            <label>
              Button Link
              <input type="text" value={content.buttonLink} onChange={(e) => updateContent('buttonLink', e.target.value)} placeholder="https://..." />
            </label>
          </div>
        );

      case 'cta':
        return (
          <div className="element-editor">
            <label>
              Headline
              <input type="text" value={content.headline} onChange={(e) => updateContent('headline', e.target.value)} />
            </label>
            <label>
              Text
              <textarea value={content.text} onChange={(e) => updateContent('text', e.target.value)} rows="2" />
            </label>
            <label>
              Button Text
              <input type="text" value={content.buttonText} onChange={(e) => updateContent('buttonText', e.target.value)} />
            </label>
            <label>
              Button Link
              <input type="text" value={content.buttonLink} onChange={(e) => updateContent('buttonLink', e.target.value)} placeholder="https://..." />
            </label>
            <label>
              Background Color
              <input type="color" value={content.bgColor} onChange={(e) => updateContent('bgColor', e.target.value)} />
            </label>
          </div>
        );

      case 'video':
        return (
          <div className="element-editor">
            <label>
              Video URL
              <input type="text" value={content.videoUrl} onChange={(e) => updateContent('videoUrl', e.target.value)} placeholder="https://..." />
            </label>
            <label>
              Thumbnail Image
              <input type="text" value={content.thumbnail} onChange={(e) => updateContent('thumbnail', e.target.value)} placeholder="https://..." />
            </label>
            <label>
              Alt Text
              <input type="text" value={content.alt} onChange={(e) => updateContent('alt', e.target.value)} />
            </label>
          </div>
        );

      case 'html':
        return (
          <div className="element-editor">
            <label>
              HTML Code
              <textarea value={content.code} onChange={(e) => updateContent('code', e.target.value)} rows="5" />
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      showNotification('Please enter a template name', 'error');
      return;
    }

    if (rows.length === 0) {
      showNotification('Please add at least one row to the template', 'error');
      return;
    }

    const templateData = {
      name: templateName,
      structure: rows
    };

    if (onSave) {
      onSave(templateData);
      return;
    }

    // Save to database
    setSaving(true);
    try {
      let response;
      if (templateId) {
        // Update existing template
        response = await api.put(`/api/email-templates/update/${templateId}`, templateData);
        showNotification('Template updated successfully!', 'success');
      } else {
        // Create new template
        response = await api.post('/api/email-templates/create', templateData);
        showNotification('Template saved successfully!', 'success');
        setTemplateId(response.data._id);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showNotification('Error saving template: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (previewMode) {
    return (
      <div className="email-builder preview-mode">
        <div className="toolbar">
          <button onClick={() => setPreviewMode(false)} className="toolbar-btn">
            <FaCode /> Back to Editor
          </button>
        </div>
        <div className="preview-container">
          <iframe
            srcDoc={generatePreviewHTML()}
            title="Email Preview"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="email-builder">
      <div className="toolbar">
        <input
          type="text"
          className="template-name-input"
          placeholder="Template Name..."
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
        <div className="toolbar-actions">
          <button onClick={() => setPreviewMode(true)} className="toolbar-btn">
            <FaEye /> Preview
          </button>
          {((templateId && (user?.role?.permissions?.canEditEmailTemplates || user?.isAdmin)) || 
            (!templateId && (user?.role?.permissions?.canCreateEmailTemplates || user?.isAdmin))) && (
            <button onClick={handleSave} className="toolbar-btn primary" disabled={saving}>
              <FaSave /> {saving ? 'Saving...' : 'Save Template'}
            </button>
          )}
        </div>
      </div>

      <div className="builder-content">
        <div className="sidebar">
          <div className="sidebar-tabs">
            <button
              className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
            >
              Basic Blocks
            </button>
            <button
              className={`tab-btn ${activeTab === 'premade' ? 'active' : ''}`}
              onClick={() => setActiveTab('premade')}
            >
              Premade
            </button>
            <button
              className={`tab-btn ${activeTab === 'structure' ? 'active' : ''}`}
              onClick={() => setActiveTab('structure')}
            >
              Structure
            </button>
          </div>

          <div className="sidebar-content">
            {activeTab === 'basic' && (
              <div className="blocks-grid">
                {basicBlocks.map((block) => (
                  <div 
                    key={block.type} 
                    className="block-info"
                    draggable
                    onDragStart={(e) => handleBlockDragStart(e, block.type)}
                  >
                    <block.icon size={24} />
                    <span>{block.label}</span>
                  </div>
                ))}
                <p className="blocks-hint">Drag blocks to columns or use + buttons</p>
              </div>
            )}

            {activeTab === 'premade' && (
              <div className="premade-blocks">
                {premadeBlocks.map((block) => (
                  <div 
                    key={block.type} 
                    className="premade-block-info"
                    draggable
                    onDragStart={(e) => handleBlockDragStart(e, block.type)}
                  >
                    {block.label}
                  </div>
                ))}
                <p className="blocks-hint">Drag blocks to columns or use + buttons</p>
              </div>
            )}

            {activeTab === 'structure' && (
              <div className="structure-section">
                <h3>Add Row</h3>
                <div className="structure-blocks">
                  <button className="structure-btn" onClick={() => addRow(1)}>
                    <AiOutlineColumnWidth size={24} />
                    <span>1 Column</span>
                  </button>
                  <button className="structure-btn" onClick={() => addRow(2)}>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <AiOutlineColumnWidth size={20} />
                      <AiOutlineColumnWidth size={20} />
                    </div>
                    <span>2 Columns</span>
                  </button>
                  <button className="structure-btn" onClick={() => addRow(3)}>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <AiOutlineColumnWidth size={16} />
                      <AiOutlineColumnWidth size={16} />
                      <AiOutlineColumnWidth size={16} />
                    </div>
                    <span>3 Columns</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="canvas">
          {rows.length === 0 ? (
            <div className="empty-canvas">
              <p>Start by adding a row structure from the Structure tab →</p>
            </div>
          ) : (
            rows.map((row, rowIndex) => (
              <div
                key={row.id}
                className="email-row"
                draggable
                onDragStart={(e) => handleDragStart(e, rowIndex)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, rowIndex)}
              >
                <div className="row-header">
                  <div className="row-left">
                    <FaGripVertical className="drag-handle" />
                    <span>Row {rowIndex + 1}</span>
                  </div>
                  <div className="row-actions">
                    <div className="split-buttons">
                      <button 
                        onClick={() => splitRowIntoColumns(rowIndex, 1)}
                        className={`split-btn ${row.columns.length === 1 ? 'active' : ''}`}
                        title="1 Column"
                      >
                        1
                      </button>
                      <button 
                        onClick={() => splitRowIntoColumns(rowIndex, 2)}
                        className={`split-btn ${row.columns.length === 2 ? 'active' : ''}`}
                        title="2 Columns"
                      >
                        2
                      </button>
                      <button 
                        onClick={() => splitRowIntoColumns(rowIndex, 3)}
                        className={`split-btn ${row.columns.length === 3 ? 'active' : ''}`}
                        title="3 Columns"
                      >
                        3
                      </button>
                    </div>
                    <button onClick={() => removeRow(rowIndex)} className="remove-row-btn">
                      <FaTrash />
                    </button>
                  </div>
                </div>
                <div className="row-columns">
                  {row.columns.map((column, columnIndex) => (
                    <div
                      key={column.id}
                      className="email-column"
                      style={{ width: column.width }}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleColumnDrop(e, rowIndex, columnIndex)}
                    >
                      {column.elements.length === 0 ? (
                        <div className="empty-column">
                          <div className="add-element-dropdown">
                            <button 
                              className="add-element-btn"
                              onClick={(e) => toggleDropdown(`dropdown-${rowIndex}-${columnIndex}`, e)}
                            >
                              <FaPlus />
                            </button>
                            {showDropdown === `dropdown-${rowIndex}-${columnIndex}` && (
                              <div className="element-dropdown" onClick={(e) => e.stopPropagation()}>
                                <div className="dropdown-header">Add Element</div>
                                <div className="dropdown-section">
                                  <div className="dropdown-label">Basic</div>
                                  {basicBlocks.map(block => (
                                    <button
                                      key={block.type}
                                      className="dropdown-item"
                                      onClick={() => addElementToColumn(rowIndex, columnIndex, block.type)}
                                    >
                                      <block.icon /> {block.label}
                                    </button>
                                  ))}
                                </div>
                                <div className="dropdown-section">
                                  <div className="dropdown-label">Premade</div>
                                  {premadeBlocks.map(block => (
                                    <button
                                      key={block.type}
                                      className="dropdown-item"
                                      onClick={() => addElementToColumn(rowIndex, columnIndex, block.type)}
                                    >
                                      {block.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          {column.elements.map((element, elementIndex) => (
                            <div
                              key={element.id}
                              className={`email-element ${selectedBlock?.rowIndex === rowIndex && selectedBlock?.columnIndex === columnIndex && selectedBlock?.elementIndex === elementIndex ? 'selected' : ''}`}
                              draggable
                              onDragStart={(e) => handleDragStart(e, rowIndex, columnIndex, elementIndex)}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, rowIndex, columnIndex, elementIndex)}
                              onClick={() => setSelectedBlock({ rowIndex, columnIndex, elementIndex })}
                            >
                              <div className="element-header">
                                <div className="element-left">
                                  <FaGripVertical className="drag-handle" />
                                  <span>{element.type}</span>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeElement(rowIndex, columnIndex, elementIndex);
                                  }}
                                  className="remove-row-btn"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                              {selectedBlock?.rowIndex === rowIndex && 
                               selectedBlock?.columnIndex === columnIndex && 
                               selectedBlock?.elementIndex === elementIndex && (
                                renderElementEditor(element, rowIndex, columnIndex, elementIndex)
                              )}
                            </div>
                          ))}
                          <div className="add-element-to-column">
                            <button 
                              className="add-more-btn"
                              onClick={(e) => toggleDropdown(`dropdown-add-${rowIndex}-${columnIndex}`, e)}
                            >
                              <FaPlus /> Add More
                            </button>
                            {showDropdown === `dropdown-add-${rowIndex}-${columnIndex}` && (
                              <div className="element-dropdown bottom" onClick={(e) => e.stopPropagation()}>
                                <div className="dropdown-header">Add Element</div>
                                <div className="dropdown-section">
                                  <div className="dropdown-label">Basic</div>
                                  {basicBlocks.map(block => (
                                    <button
                                      key={block.type}
                                      className="dropdown-item"
                                      onClick={() => addElementToColumn(rowIndex, columnIndex, block.type)}
                                    >
                                      <block.icon /> {block.label}
                                    </button>
                                  ))}
                                </div>
                                <div className="dropdown-section">
                                  <div className="dropdown-label">Premade</div>
                                  {premadeBlocks.map(block => (
                                    <button
                                      key={block.type}
                                      className="dropdown-item"
                                      onClick={() => addElementToColumn(rowIndex, columnIndex, block.type)}
                                    >
                                      {block.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default EmailBuilder;
